import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Mail, Globe, Instagram, ShieldCheck
} from 'lucide-react';
import Auth3DShowcase from './Auth3DShowcase';
import PricingPlans from './PricingPlans';
import { useAuth } from '../../context/AuthContext';
import { fetchBillingConfig, startSubscription } from '../../lib/billing';
import './login.css';

/**
 * Purchase / Plans page — single source of truth for plans is
 * `PricingPlans.jsx`. Same cinematic split layout as Login / Signup.
 *
 * Reachable from:
 *   - SignupPage (when the email is already taken)
 *   - LoginPage  (small "Get Premium" footer link)
 *   - Settings   (every signed-in user can upgrade from here)
 *   - Direct URL: /purchase  or  /pricing
 */

export default function PurchasePage() {
  const { isAuthenticated, user, setUser, token } = useAuth();
  const navigate = useNavigate();
  const [billingLive, setBillingLive] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchBillingConfig().then((c) => setBillingLive(!!c.configured));
  }, []);

  // pro-monthly → monthly, pro-yearly → yearly
  const planKey = (id) => (id === 'pro-yearly' ? 'yearly' : 'monthly');

  const handleSelectPaid = async (planId) => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/purchase' } });
      return;
    }
    if (busy) return;
    setBusy(true);
    try {
      const res = await startSubscription(planKey(planId), {
        name: user?.name || '',
        email: user?.email || '',
      });
      if (res.status === 'success') {
        // Refresh the user so the new subscription_end propagates and gates unlock.
        try {
          const me = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (me.ok) setUser(await me.json());
        } catch { /* non-fatal */ }
        toast.success('Subscription active — welcome to Pro!');
        navigate('/app');
      } else if (res.status === 'dismissed') {
        toast.message('Checkout cancelled');
      } else {
        toast.error(res.message || 'Payment failed');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="h-[100dvh] w-full bg-black grid lg:grid-cols-[1fr_minmax(540px,0.85fr)] overflow-hidden"
      data-testid="purchase-page"
    >
      <Auth3DShowcase
        headline={<>Pick the <span className="auth-glass-title">right plan</span> for your studio.</>}
        subline={billingLive
          ? 'Start free, upgrade when your shows go live. Secure auto-billing via Razorpay — cancel anytime.'
          : 'Start free, upgrade when your shows go live. Manual activation by our team — no auto-billing yet.'}
      />

      <div
        className="relative flex items-start justify-center p-6 sm:p-10 auth-grain overflow-y-auto"
        style={{ background: 'radial-gradient(ellipse at center, #0a0d1c 0%, #05060f 60%, #000 100%)' }}
      >
        <div className="absolute left-1/2 top-1/3 -translate-x-1/2 -translate-y-1/2 w-[520px] h-[520px] rounded-full bg-fuchsia-600/10 blur-3xl pointer-events-none" />
        <div className="absolute left-1/3 top-1/3 w-[360px] h-[360px] rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />

        <div className="relative w-full max-w-2xl auth-fade-up py-4">
          <div className="flex flex-col items-center mb-6">
            <img src="/branding/avst-logo.png" alt="AV Simulation Tool"
                 className="h-20 w-auto mb-2 select-none drop-shadow-[0_0_24px_rgba(56,189,248,0.25)]" draggable={false} />
            <p className="text-[11px] text-zinc-500 tracking-[0.28em] uppercase">Powered by Ideation Audio Visual</p>
          </div>

          <div className="text-center mb-6">
            <h1 className="text-2xl font-semibold text-white">Plans &amp; Pricing</h1>
            <p className="text-zinc-400 text-sm mt-1">
              {billingLive
                ? 'Choose a plan and unlock the full 3D Simulation instantly.'
                : 'Choose a plan and our team will activate your account within 24 hours.'}
            </p>
          </div>

          <PricingPlans onSelectPaid={billingLive ? handleSelectPaid : null} />

          {/* Quick contact strip */}
          <div className="mt-6 auth-glass rounded-xl p-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-zinc-300">
            <span className="inline-flex items-center gap-2 text-cyan-300/90">
              <ShieldCheck className="w-3.5 h-3.5" /> {billingLive ? 'Secure auto-billing · cancel anytime' : 'Manual activation, no auto-billing'}
            </span>
            <a href="mailto:admin@ideationav.com" className="inline-flex items-center gap-1.5 hover:text-cyan-300 transition-colors" data-testid="purchase-mailto">
              <Mail className="w-3.5 h-3.5" /> admin@ideationav.com
            </a>
          </div>

          <div className="mt-5 flex items-center justify-center gap-4">
            <Link to="/login" className="text-xs text-zinc-400 hover:text-cyan-300 transition-colors tracking-wider" data-testid="purchase-back-login">
              <ArrowLeft className="w-3 h-3 inline mr-1" /> Back to Sign In
            </Link>
            <span className="text-zinc-700 text-xs">•</span>
            <Link to="/signup" className="text-xs text-zinc-400 hover:text-cyan-300 transition-colors tracking-wider" data-testid="purchase-to-signup">
              Or start the free demo →
            </Link>
          </div>

          {/* Footer */}
          <div className="mt-8 text-center space-y-3">
            <div className="flex items-center justify-center gap-4 text-[11px] text-zinc-500">
              <a href="https://www.ideationav.com" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-cyan-300 transition-colors">
                <Globe className="w-3 h-3" /> www.ideationav.com
              </a>
              <span className="text-zinc-700">•</span>
              <a href="https://www.instagram.com/avsimulationtool/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-fuchsia-300 transition-colors">
                <Instagram className="w-3 h-3" /> @avsimulationtool
              </a>
            </div>
            <p className="text-[10px] text-zinc-600 leading-relaxed">© 2026 Ideation Audio Visual. All rights reserved.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
